import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import LinearProgress from '@mui/material/LinearProgress';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import RefreshIcon from '@mui/icons-material/Refresh';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import SettingsIcon from '@mui/icons-material/Settings';
import CloseIcon from '@mui/icons-material/Close';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import VideoEditingService, {
  VideoClip, SourceContext, LoadRequest, ClipSegment, BrollAsset,
  ClipCategory, ClipLabel, ClipLabelEdit, ClipLabelVocabulary, LibraryClipLabel, NewClipLabel,
} from 'services/VideoEditingService';
import { useClipPreviewStatus } from './useClipPreviewStatus';
import BrollPickerDialog from './components/BrollPickerDialog';
import ClipEditDialog, { ClipEditSave } from 'pages/BrollLibrary/ClipEditDialog';
import { brollAssetFrom, brollSegmentFrom, brollSourceClipFrom } from './brollAssets';
import VideoCreativeService from 'services/VideoCreativeService';
import { StoryboardSegment } from 'pages/VideoCreative/types';
import SourceSelector from './components/SourceSelector';
import ClipBrowser, { LinkedTimelineClip, NewClipSegment } from './components/ClipBrowser';
import TimelineEditor, { TimelineEditorHandle } from './components/TimelineEditor';
import AutoTranscribeDialog from './components/AutoTranscribeDialog';
import { STAGE_MAX_HEIGHT } from './layout';
import {
  loadVideoEditingFontSize,
  saveVideoEditingFontSize,
  scaleFontSize,
  VIDEO_EDITING_FONT_SCALES,
  VideoEditingFontScaleContext,
  VideoEditingFontSize,
} from './fontScale';

const videoEditingService = new VideoEditingService();
const ciService = new VideoCreativeService();

const driveFolderLink = (folderId: string) => `https://drive.google.com/drive/folders/${folderId}`;

// Source labels can embed the pasted Drive URL ("Madi - https://drive.google…").
// The compact project bar shows just the human part; the link itself is the
// separate "Open in Google Drive" action.
const displaySourceLabel = (label?: string) => {
  const cleaned = (label || '').replace(/https?:\/\/\S+/g, '').replace(/[\s\-–—·|:]+$/g, '').trim();
  return cleaned || 'Drive folder';
};
const TYPOGRAPHY_VARIANTS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'subtitle1', 'subtitle2', 'body1', 'body2', 'button', 'caption', 'overline'] as const;

// Video Editing dashboard. Phase 1: pick a source (a Drive folder or a sent
// script) and browse/preview the raw clips. Phase 2: lay those clips out into a
// saved edit plan (cuts + audio overlays mapped to script beats) and export
// instructions for the external editor.
export default function VideoEditingPage() {
  const baseTheme = useTheme();
  const [dashboardFontSize, setDashboardFontSize] = useState<VideoEditingFontSize>(loadVideoEditingFontSize);
  const fontScale = VIDEO_EDITING_FONT_SCALES[dashboardFontSize];
  const dashboardTheme = useMemo(() => createTheme(baseTheme, {
    typography: Object.fromEntries(TYPOGRAPHY_VARIANTS.map((variant) => [
      variant,
      { fontSize: scaleFontSize(baseTheme.typography[variant].fontSize, fontScale) },
    ])),
  }), [baseTheme, fontScale]);
  const changeDashboardFontSize = (_event: React.MouseEvent<HTMLElement>, value: VideoEditingFontSize | null) => {
    if (!value) return;
    setDashboardFontSize(value);
    saveVideoEditingFontSize(value);
  };
  const [clips, setClips] = useState<VideoClip[] | null>(null);
  const [clipsLoading, setClipsLoading] = useState(false);
  const [clipsError, setClipsError] = useState<string | null>(null);
  const [source, setSource] = useState<SourceContext | null>(null);
  const [segments, setSegments] = useState<StoryboardSegment[] | null>(null);
  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);
  // Source fileIds currently used on the edit timeline (reported by the editor), so
  // the browser can flag which clips are already in the edit.
  const [usedFileIds, setUsedFileIds] = useState<string[]>([]);
  const [usedSegmentIds, setUsedSegmentIds] = useState<string[]>([]);
  // Saved sub-clips owned by the active plan. Legacy folder-level records are
  // loaded separately and imported only by older plans that predate isolation.
  const [clipSegments, setClipSegments] = useState<ClipSegment[]>([]);
  // Library b-roll pulled into the open plan, and the resolved library records behind it. The
  // assets are stored in the plan; the records are re-fetched, because their signed URLs expire.
  const [brollAssets, setBrollAssets] = useState<BrollAsset[]>([]);
  const [brollLabels, setBrollLabels] = useState<LibraryClipLabel[]>([]);
  // The source files behind that b-roll, for the timeline to resolve playback through and for
  // the transcode poll below. They are NOT part of the project's clip list — that drives the
  // Video and Audio tabs, and a file from another creator's folder appearing there would be
  // wrong. Declared here, above the poll that depends on it.
  const brollClips = useMemo(() => brollLabels.map(brollSourceClipFrom), [brollLabels]);
  const [brollPickerOpen, setBrollPickerOpen] = useState(false);
  // The pulled-in clip whose LIBRARY record is open for editing, if any.
  const [editingBrollLabel, setEditingBrollLabel] = useState<LibraryClipLabel | null>(null);
  const [planProductCategory, setPlanProductCategory] = useState<string | null>(null);
  const [clipLabels, setClipLabels] = useState<Record<string, ClipLabel[]>>({});
  const [clipLabelVocabulary, setClipLabelVocabulary] = useState<ClipLabelVocabulary | null>(null);
  const [clipLabelsLoading, setClipLabelsLoading] = useState(false);
  const [clipCategories, setClipCategories] = useState<ClipCategory[] | null>(null);
  const [legacyClipSegments, setLegacyClipSegments] = useState<ClipSegment[]>([]);
  const [selectedSegmentId, setSelectedSegmentId] = useState<string | null>(null);
  // The central stage always plays the final composed video; editing a specific
  // clip opens this modal so it's clear which clip is being worked on.
  const [clipEditorOpen, setClipEditorOpen] = useState(false);
  // The repeated full timeline at the bottom of the clip-editor modal is hidden
  // by default (the modal is about the one clip) and expandable on demand.
  const [modalTimelineExpanded, setModalTimelineExpanded] = useState(false);
  // The project picker collapses into a compact bar once a project is loaded.
  const [selectorExpanded, setSelectorExpanded] = useState(true);
  const [linkedTimelineClip, setLinkedTimelineClip] = useState<LinkedTimelineClip | null>(null);
  // The "Note for editor" text for the clip being edited in the modal, mirrored
  // from the selected clip and written back to it as the user types.
  const [editorNote, setEditorNote] = useState('');
  // Null means the folder's full default clip set. Once a plan opens, its saved
  // membership controls which source clips appear in the Clips tab.
  const [planClipIds, setPlanClipIds] = useState<string[] | null>(null);
  const [planControlsContainer, setPlanControlsContainer] = useState<HTMLDivElement | null>(null);
  const handlePlanControlsContainer = useCallback((node: HTMLDivElement | null) => setPlanControlsContainer(node), []);
  // Slots for the unified workspace: the clip browser's list renders into the
  // left rail, its preview/trim card and the timeline's composed preview render
  // into the two swappable layers of the shared stage.
  const [libraryContainer, setLibraryContainer] = useState<HTMLDivElement | null>(null);
  const handleLibraryContainer = useCallback((node: HTMLDivElement | null) => setLibraryContainer(node), []);
  const [inspectorContainer, setInspectorContainer] = useState<HTMLDivElement | null>(null);
  const handleInspectorContainer = useCallback((node: HTMLDivElement | null) => setInspectorContainer(node), []);
  const [stageContainer, setStageContainer] = useState<HTMLDivElement | null>(null);
  const handleStageContainer = useCallback((node: HTMLDivElement | null) => setStageContainer(node), []);
  // While the clip editor modal is open, the timeline dock renders into this
  // slot (inside the modal) so all tracks are editable there; otherwise it
  // renders in the page dock below the stage.
  const [modalTimelineContainer, setModalTimelineContainer] = useState<HTMLDivElement | null>(null);
  const handleModalTimelineContainer = useCallback((node: HTMLDivElement | null) => setModalTimelineContainer(node), []);
  // The toolbar row holding the project name — the scroll target after a load.
  const projectRowRef = useRef<HTMLDivElement>(null);
  // When a load opens a specific saved project, this carries its id to the timeline
  // editor; loadNonce bumps on every load so the editor re-opens even when the
  // picked project's folder is already the loaded one.
  const [pendingOpenPlanId, setPendingOpenPlanId] = useState<string | null>(null);
  const [loadNonce, setLoadNonce] = useState(0);
  // Uploading assets into the current Drive folder.
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const uploadInputRef = useRef<HTMLInputElement>(null);
  // Auto-transcribe folders settings dialog (gear in the header).
  const [autoTranscribeOpen, setAutoTranscribeOpen] = useState(false);
  const visibleClips = clips == null || planClipIds == null
    ? clips
    : clips.filter((clip) => planClipIds.includes(clip.fileId));

  // The clip browser's own inline player (independent of the timeline editor's
  // composed preview, which has its own players).
  const playerRef = useRef<HTMLVideoElement>(null);
  // The timeline editor, so "Add to timeline" from the browser reaches it.
  const editorRef = useRef<TimelineEditorHandle>(null);

  // AI clip labels for the open folder, keyed by fileId, plus the vocabulary the
  // edit form's dropdowns offer. Labels are generated per folder by a separate job,
  // so a folder that hasn't been labeled yet simply loads an empty map.
  useEffect(() => {
    const folderId = source?.folderId;
    if (!folderId) { setClipLabels({}); return undefined; }
    let stopped = false;
    setClipLabelsLoading(true);
    (async () => {
      const [labelResp, vocab] = await Promise.all([
        videoEditingService.listClipLabels(folderId),
        clipLabelVocabulary ? Promise.resolve(clipLabelVocabulary) : videoEditingService.getClipLabelVocabulary(),
      ]);
      if (stopped) return;
      const byFile: Record<string, ClipLabel[]> = {};
      for (const label of labelResp?.labels || []) {
        if (!byFile[label.fileId]) byFile[label.fileId] = [];
        byFile[label.fileId].push(label);
      }
      setClipLabels(byFile);
      if (vocab) setClipLabelVocabulary(vocab);
      setClipLabelsLoading(false);
    })();
    return () => { stopped = true; };
    // clipLabelVocabulary is intentionally not a dependency — it's fetched once and
    // reused; re-running on it would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source?.folderId]);

  // Save one label's corrections and swap the returned row into place, so the panel
  // shows exactly what was stored (including the human-edited marker).
  async function saveClipLabel(labelId: string, edit: ClipLabelEdit): Promise<void> {
    const { label } = await videoEditingService.updateClipLabel(labelId, edit);
    setClipLabels(prev => ({
      ...prev,
      [label.fileId]: (prev[label.fileId] || []).map(l => (l.labelId === label.labelId ? label : l)),
    }));
  }

  // The human category vocabulary, loaded once for the whole page (it's the same list
  // whichever folder is open).
  useEffect(() => {
    videoEditingService.listClipCategories().then(r => { if (r) setClipCategories(r.categories); });
  }, []);

  const createClipCategory = useCallback(async (name: string) => {
    const { category } = await videoEditingService.createClipCategory(name);
    setClipCategories(prev => {
      const rest = (prev || []).filter(c => c.slug !== category.slug);
      return [...rest, category].sort((a, b) => a.name.localeCompare(b.name));
    });
    return category;
  }, []);

  // Add a trimmed range to the b-roll library. The new label lands on the same Drive file,
  // so fold it into the open folder's labels too — otherwise the clip's Labels panel would
  // keep showing the old set until the folder is reloaded.
  const addToBrollLibrary = useCallback(async (input: NewClipLabel) => {
    const { label } = await videoEditingService.createClipLabel(input);
    setClipLabels(prev => ({
      ...prev,
      [label.fileId]: [...(prev[label.fileId] || []), label],
    }));
  }, []);

  // Pre-warm transcoding for every video clip and follow it, so previews are ready (or
  // visibly on their way) before the editor clicks one.
  //
  // Borrowed b-roll is included even though the project doesn't own those files: the status
  // endpoint is keyed by Drive fileId alone, so asking about another creator's file is fine, and
  // it queues a transcode for any that has never had one. Leaving them out was what made a
  // pulled-in clip sit on "Preparing preview…" forever — the player waits for a status that was
  // never going to arrive, and the filmstrip waits on the player.
  const {
    statuses: previewStatuses,
    errors: previewErrors,
    retry: retryPreview,
    retryAll,
    retryingAll: retryingAllPreviews,
  } = useClipPreviewStatus([...(clips || []), ...brollClips]);

  // Only the project's own clips get the bulk retry — borrowed b-roll is retried from the
  // card it appears on, and a "Retry 3" button that silently also touched another creator's
  // folder would be lying about what it did.
  const retryAllPreviews = useCallback(() => retryAll(clips || []), [retryAll, clips]);

  // "Refresh clips" — re-list the current folder past the server's 1h cache.
  async function refreshClips() {
    if (!source) return;
    setClipsLoading(true);
    setClipsError(null);
    try {
      const resp = await videoEditingService.listClips({ folderId: source.folderId, refresh: true });
      setClips(resp.clips);
      if (resp.clips.length > 0 && !resp.clips.some(c => c.fileId === selectedFileId)) {
        setSelectedFileId(resp.clips[0].fileId);
        setSelectedSegmentId(null);
      }
    } catch (err: any) {
      setClipsError(err?.response?.data?.error || err?.message || 'Failed to refresh clips');
    } finally {
      setClipsLoading(false);
    }
  }

  // Upload files into the current Drive folder, then refresh so they're picked up
  // (listed + transcoded). Surfaces the backend's actionable error (e.g. a folder
  // shared read-only) and any per-file failures rather than failing silently.
  async function handleUploadFiles(files: FileList | null) {
    if (!source || !files || files.length === 0) return;
    setUploading(true);
    setUploadProgress(0);
    setClipsError(null);
    try {
      const res = await videoEditingService.uploadClips(source.folderId, Array.from(files), setUploadProgress);
      await refreshClips();
      if (res.failed.length > 0) {
        setClipsError(`Couldn't upload ${res.failed.length} file(s): ${res.failed.map(f => `${f.name} (${f.error})`).join('; ')}`);
      }
    } catch (err: any) {
      setClipsError(err?.response?.data?.error || err?.message || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (uploadInputRef.current) uploadInputRef.current.value = '';
    }
  }

  // "Extract audio" in the clip browser: split a video clip's audio into its own
  // Drive file (reusing the video's transcript when it's already done), surface
  // it in the clip list and the open plan's working set, and select it.
  async function extractClipAudio(clip: VideoClip) {
    if (!source) return null;
    const res = await videoEditingService.extractClipAudio(clip.fileId, source.folderId);
    setClips((current) => {
      if (!current || current.some((c) => c.fileId === res.clip.fileId)) return current;
      return [...current, res.clip];
    });
    editorRef.current?.addSourceToPlan(res.clip.fileId);
    setSelectedFileId(res.clip.fileId);
    setSelectedSegmentId(null);
    return res;
  }

  // ── Saved clip segments (persisted inside the active plan) ──

  async function saveClipSegment(input: NewClipSegment): Promise<boolean> {
    const segment = await editorRef.current?.saveSegment(input);
    if (!segment) return false;
    setSelectedFileId(segment.sourceFileId);
    setSelectedSegmentId(segment.segmentId);
    editorRef.current?.selectClipForSegment(segment.segmentId);
    return true;
  }

  async function removeClipSegment(segmentId: string): Promise<boolean> {
    const removed = editorRef.current?.deleteSegment(segmentId) ?? false;
    if (!removed) return false;
    setSelectedSegmentId(current => (current === segmentId ? null : current));
    return true;
  }

  async function trimSavedClip(segment: ClipSegment, inSec: number, outSec: number): Promise<boolean> {
    const updated = editorRef.current?.trimSegmentClip(segment.segmentId, inSec, outSec) ?? false;
    setClipsError(updated ? null : 'The saved clip range could not be updated. Try again.');
    return updated;
  }

  // Drop a saved segment onto the timeline as a pre-trimmed clip.
  // Borrowed b-roll files are deliberately absent from `clips`, so they are looked up here too —
  // without that, "Add to timeline" on a pulled-in clip found no source and quietly did nothing
  // while still reporting success. Returns whether it was actually placed, so the caller can
  // stop claiming otherwise.
  async function addSegmentToTimeline(seg: ClipSegment): Promise<boolean> {
    const src = [...(clips || []), ...brollClips].find(c => c.fileId === seg.sourceFileId);
    if (!src) {
      console.error('[video-editing] no source clip for segment', seg.segmentId, seg.sourceFileId);
      return false;
    }
    return (await editorRef.current?.addSegmentClip(src, seg)) ?? false;
  }

  async function handleLoadClips(req: LoadRequest) {
    setClipsLoading(true);
    setClipsError(null);
    setSelectedFileId(null);
    setSegments(null);
    setSource(null);
    setUsedFileIds([]);
    setUsedSegmentIds([]);
    setClipSegments([]);
    setLegacyClipSegments([]);
    setSelectedSegmentId(null);
    setLinkedTimelineClip(null);
    setPlanClipIds(null);
    setClipEditorOpen(false);
    // One-shot: this load opens req.openPlanId (if any) and nothing later; bump the
    // nonce so the timeline editor re-runs its open effect even for the same folder.
    setPendingOpenPlanId(req.openPlanId ?? null);
    setLoadNonce((n) => n + 1);
    try {
      // A DRIVE source picked from a saved project / recently-used folder already
      // knows its folderId, so load by id and skip re-resolving the pasted URL.
      const resp = await videoEditingService.listClips(
        req.sourceType === 'DRIVE'
          ? (req.folderId ? { folderId: req.folderId } : { driveUrl: req.driveUrl })
          : { folderId: req.folderId },
      );
      if (resp.needsDeliveryChoice) {
        setClipsError('This script was sent to several creators — pick one above first.');
        setClips(null);
        return;
      }
      const folderId = resp.folderId || (req.sourceType === 'STORYBOARD' ? req.folderId : '');
      const ctx: SourceContext = {
        sourceType: req.sourceType,
        folderId,
        folderUrl: req.sourceType === 'STORYBOARD' ? (req.folderUrl || driveFolderLink(folderId)) : driveFolderLink(folderId),
        storyboardId: req.sourceType === 'STORYBOARD' ? req.storyboardId : undefined,
        deliveryId: req.sourceType === 'STORYBOARD' ? req.deliveryId : undefined,
        label: req.label,
      };
      setClips(resp.clips);
      if (resp.clips.length > 0) setSelectedFileId(resp.clips[0].fileId);

      // Compatibility only: older plans stored saved clips at folder scope. New
      // plans never write here and receive their own empty saved-clip collection.
      // Finish this read before mounting the plan editor so a fast first edit
      // cannot materialize an incomplete legacy list.
      const legacyResp = await videoEditingService.listClipSegments(folderId);
      if (!legacyResp) throw new Error('Failed to load saved clips for this source');
      setLegacyClipSegments(legacyResp.segments || []);
      setSource(ctx);
      // The project is in — hand the vertical space to the workspace.
      setSelectorExpanded(false);

      // For a script source, load its segments so beats can map to the script.
      if (ctx.storyboardId) {
        const sb = await ciService.getStoryboard(ctx.storyboardId);
        setSegments(sb?.storyboard?.segments || []);
      }
    } catch (err: any) {
      setClipsError(err?.response?.data?.error || err?.message || 'Failed to load clips');
      setClips(null);
    } finally {
      setClipsLoading(false);
    }
  }

  // Single-click on a timeline clip just selects it (for split/delete/etc.).
  // Opening the editor modal is a double-click (handleTimelineClipEdit below).
  const handleTimelineClipChange = useCallback((clip: LinkedTimelineClip | null) => {
    setLinkedTimelineClip(clip);
    setEditorNote(clip?.note ?? '');
    if (clip) {
      setSelectedFileId(clip.sourceFileId);
      setSelectedSegmentId(clip.segmentId ?? null);
    }
  }, []);

  const handleTimelineClipEdit = useCallback(() => {
    editorRef.current?.pausePreview();
    setClipEditorOpen(true);
  }, []);

  const handleUsedClipsChange = useCallback((fileIds: string[], segmentIds: string[]) => {
    setUsedFileIds(fileIds);
    setUsedSegmentIds(segmentIds);
  }, []);

  const handlePlanClipsChange = useCallback((fileIds: string[]) => {
    setPlanClipIds(fileIds);
    const visibleIds = new Set(fileIds);
    setSelectedFileId((current) => (current && visibleIds.has(current) ? current : fileIds[0] ?? null));
    setLinkedTimelineClip((current) => (current && visibleIds.has(current.sourceFileId) ? current : null));
  }, []);

  const handlePlanBrollAssetsChange = useCallback((assets: BrollAsset[]) => {
    setBrollAssets(assets);
  }, []);

  // Re-mint the signed stream and poster URLs for the plan's b-roll. They expire, so they are
  // never stored with the plan — only the ids are, and those are resolved here on open and
  // after any change.
  useEffect(() => {
    const ids = brollAssets.map(a => a.labelId);
    if (!ids.length) { setBrollLabels([]); return undefined; }
    let stopped = false;
    videoEditingService.resolveClipLabels(ids).then((resp) => {
      if (stopped || !resp) return;
      setBrollLabels(resp.labels);
    });
    return () => { stopped = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brollAssets.map(a => a.labelId).join(',')]);

  // Pull one library clip in: the asset record plus the segment that makes it draggable.
  const addBrollAsset = useCallback(async (label: LibraryClipLabel) => {
    const segment = brollSegmentFrom(label);
    if (!segment) throw new Error('That clip has no known duration yet, so it has no range to place.');
    editorRef.current?.addBrollAsset(brollAssetFrom(label), segment);
  }, []);

  // Dropping a pulled-in clip takes the asset, its segment and every placement with it. So the
  // clip editor closes and the selection clears: leaving them would leave the modal inspecting
  // a clip that no longer exists in this project.
  function removeBrollAsset(labelId: string) {
    editorRef.current?.removeBrollAsset(labelId);
    setSelectedFileId(null);
    closeClipEditor();
  }

  // Edit the LIBRARY record behind a pulled-in clip — the same dialog the library page uses, so
  // the two can't drift on what editing a clip means. This is the shared edit: every project
  // using the clip sees it. Trimming in the inspector is the local one.
  const saveBrollLibraryClip = useCallback(async (labelId: string, save: ClipEditSave) => {
    await videoEditingService.updateClipLabel(labelId, save.edit);
    // Re-resolve rather than swapping in what the update returned: that response is a plain
    // label with no signed poster or stream URL, and moving the range regenerates the poster.
    const resp = await videoEditingService.resolveClipLabels([labelId]);
    const fresh = resp?.labels?.[0];
    if (fresh) setBrollLabels(prev => prev.map(l => (l.labelId === labelId ? fresh : l)));
    setEditingBrollLabel(null);
  }, []);

  // Carving a new library clip out of a borrowed one. It joins the library, not this project —
  // pulling it in is a separate, deliberate step.
  const saveBrollLibraryClipAsNew = useCallback(async (
    source: LibraryClipLabel, range: { inSec: number; outSec: number }, edit: ClipLabelEdit,
  ) => {
    await videoEditingService.createClipLabel({
      ...edit,
      fileId: source.fileId,
      inSec: range.inSec,
      outSec: range.outSec,
      creationSource: 'library_trim',
    });
    setEditingBrollLabel(null);
  }, []);

  const handlePlanSegmentsChange = useCallback((planSegments: ClipSegment[]) => {
    setClipSegments(planSegments);
    const segmentIds = new Set(planSegments.map((segment) => segment.segmentId));
    setSelectedSegmentId((current) => (current && !segmentIds.has(current) ? null : current));
    setLinkedTimelineClip((current) => (
      current?.segmentId && !segmentIds.has(current.segmentId) ? null : current
    ));
  }, []);

  function selectSourceClip(clip: VideoClip) {
    setSelectedFileId(clip.fileId);
    setSelectedSegmentId(null);
    editorRef.current?.selectClipForSource(clip.fileId);
    openClipEditor();
  }

  function selectSavedClip(segment: ClipSegment) {
    setSelectedFileId(segment.sourceFileId);
    setSelectedSegmentId(segment.segmentId);
    editorRef.current?.selectClipForSegment(segment.segmentId);
    openClipEditor();
  }

  function trimLinkedTimelineClip(clip: VideoClip, inSec: number, outSec: number) {
    return editorRef.current?.trimSourceClip(clip.fileId, inSec, outSec) ?? false;
  }

  // ── Clip editor modal. The central stage only ever plays the final composed
  // video; editing a specific clip happens in its own modal so it's clear what's
  // being worked on. Opening pauses the composed preview so audio doesn't stack. ──
  function openClipEditor() {
    editorRef.current?.pausePreview();
    setClipEditorOpen(true);
  }

  function closeClipEditor() {
    playerRef.current?.pause();
    setClipEditorOpen(false);
    setModalTimelineExpanded(false);
    setSelectedSegmentId(null);
    editorRef.current?.clearSelection();
  }

  // After a successful load, bring the workspace (project name row) into view —
  // the project picker above it can otherwise leave it below the fold.
  useEffect(() => {
    if (!source || clipsLoading) return;
    projectRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [loadNonce, source, clipsLoading]);

  return (
    <ThemeProvider theme={dashboardTheme}>
      <VideoEditingFontScaleContext.Provider value={fontScale}>
      <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Video Editing</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>Text size</Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={dashboardFontSize}
            onChange={changeDashboardFontSize}
            aria-label="Video dashboard text size"
          >
            <ToggleButton value="small" aria-label="Small text">Small</ToggleButton>
            <ToggleButton value="medium" aria-label="Medium text">Medium</ToggleButton>
            <ToggleButton value="large" aria-label="Large text">Large</ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title="Auto-transcribe folders">
            <IconButton size="small" onClick={() => setAutoTranscribeOpen(true)} aria-label="Auto-transcribe settings">
              <SettingsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <AutoTranscribeDialog open={autoTranscribeOpen} onClose={() => setAutoTranscribeOpen(false)} />

      {/* Project picker. Collapses into a compact bar once a project is loaded
          so the editing workspace gets the vertical space. */}
      <Collapse in={selectorExpanded}>
        <SourceSelector loadingClips={clipsLoading} onLoadClips={handleLoadClips} />
      </Collapse>
      {source && !selectorExpanded && (
        <Paper variant="outlined" sx={{ mb: 2, px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>{displaySourceLabel(source.label)}</Typography>
          {source.folderUrl && (
            <Link href={source.folderUrl} target="_blank" rel="noopener" variant="caption">
              Open in Google Drive
            </Link>
          )}
          <Button size="small" sx={{ ml: 'auto' }} onClick={() => setSelectorExpanded(true)}>
            Switch project
          </Button>
        </Paper>
      )}

      {clipsLoading && <LinearProgress sx={{ mb: 2 }} />}
      {clipsError && <Alert severity="error" sx={{ mb: 2 }}>{clipsError}</Alert>}

      {clips && !clipsLoading && (
        clips.length === 0 ? (
          <Alert severity="info">No video or audio clips in this folder yet.</Alert>
        ) : (
          <Box>
            {/* Toolbar row: the open project's controls (name, status, save
                state) render into this slot. This is the scroll target after a
                load — the workspace starts here. */}
            <Box ref={projectRowRef} sx={{ scrollMarginTop: 80, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
              <Box ref={handlePlanControlsContainer} sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }} />
            </Box>

            {/* Workspace: clip library rail beside the shared stage. */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
              {/* Left rail — the project's clip library (card grid). Sticky so it
                  stays reachable while scrolling a tall timeline, and capped to
                  the same height as the video stage beside it (own scrollbar). */}
              <Box sx={{
                width: { xs: '100%', md: 480 }, flexShrink: 0, alignSelf: 'flex-start',
                position: { md: 'sticky' }, top: { md: 80 }, maxHeight: { md: STAGE_MAX_HEIGHT },
                display: 'flex', flexDirection: 'column', gap: 1, minHeight: 0,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minHeight: 34, flexWrap: 'nowrap' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Assets ({visibleClips?.length ?? 0})
                  </Typography>
                  {/* Transcode progress lives inline next to the count while any
                      preview is still preparing; nothing shows once all are ready. */}
                  {(() => {
                    const videoClips = clips.filter(c => c.mimeType.startsWith('video/'));
                    const ready = videoClips.filter(c => previewStatuses[c.fileId] === 'ready').length;
                    const failed = videoClips.filter(c => previewStatuses[c.fileId] === 'error').length;
                    if (videoClips.length === 0 || ready + failed >= videoClips.length) {
                      return failed > 0 ? (
                        <Button size="small" variant="text" color="error" onClick={retryAllPreviews} disabled={retryingAllPreviews}
                          startIcon={retryingAllPreviews ? <CircularProgress size={12} /> : <RefreshIcon sx={{ fontSize: 14 }} />} sx={{ py: 0, minWidth: 0 }}>
                          Retry {failed}
                        </Button>
                      ) : null;
                    }
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                        <CircularProgress size={12} />
                        <Typography variant="caption" sx={{ fontSize: 11 }}>preparing {ready}/{videoClips.length}</Typography>
                      </Box>
                    );
                  })()}
                  <Box sx={{ flexGrow: 1 }} />
                  <Button
                    size="small"
                    component="label"
                    startIcon={uploading ? <CircularProgress size={14} color="inherit" /> : <UploadFileIcon fontSize="small" />}
                    disabled={uploading || clipsLoading}
                  >
                    {uploading ? `${uploadProgress}%` : 'Upload'}
                    <input
                      ref={uploadInputRef}
                      type="file"
                      accept="video/*,audio/*"
                      multiple
                      hidden
                      onChange={(e) => handleUploadFiles(e.target.files)}
                    />
                  </Button>
                  <Button size="small" startIcon={<RefreshIcon fontSize="small" />} onClick={refreshClips} disabled={clipsLoading || uploading}>
                    Refresh
                  </Button>
                </Box>
                {/* The clip list renders into this slot (scrolls within the rail). */}
                <Box ref={handleLibraryContainer} sx={{ overflowY: 'auto', minHeight: 0 }} />
              </Box>

              {/* The stage plays ONLY the final composed video. Fixed height,
                  matching the clip rail beside it. Editing a clip happens in a
                  modal, not here. */}
              <Box data-testid="stage-timeline-layer" sx={{ flexGrow: 1, minWidth: 0, width: '100%', position: 'relative', height: STAGE_MAX_HEIGHT }}>
                <Box ref={handleStageContainer} sx={{ height: '100%' }} />
              </Box>
            </Box>

            {/* Timeline dock: transport + the multi-track canvas, always visible
                at full width. Clicking a clip here opens the clip editor modal. */}
            {source && (
              <Box sx={{ mt: 2 }}>
                <TimelineEditor
                  ref={editorRef}
                  clips={clips}
                  extraClips={brollClips}
                  source={source}
                  segments={segments}
                  onUsedClipsChange={handleUsedClipsChange}
                  onSelectedClipChange={handleTimelineClipChange}
                  onRequestClipEdit={handleTimelineClipEdit}
                  onPlanClipsChange={handlePlanClipsChange}
                  onPlanSegmentsChange={handlePlanSegmentsChange}
                  onPlanBrollAssetsChange={handlePlanBrollAssetsChange}
                  onPlanProductCategoryChange={setPlanProductCategory}
                  legacyClipSegments={legacyClipSegments}
                  clipLabelVocabulary={clipLabelVocabulary}
                  planControlsContainer={planControlsContainer}
                  stageContainer={stageContainer}
                  timelineContainer={clipEditorOpen && modalTimelineExpanded ? modalTimelineContainer : null}
                  initialPlanId={pendingOpenPlanId}
                  loadNonce={loadNonce}
                  previewStatuses={previewStatuses}
                />
              </Box>
            )}

            {brollPickerOpen && (
              <BrollPickerDialog
                // Pre-filtered to the project's own product: a project about mattress toppers
                // almost always wants topper footage. A starting point, not a restriction.
                productCategory={planProductCategory}
                alreadyAdded={new Set(brollAssets.map(a => a.labelId))}
                onAdd={addBrollAsset}
                onClose={() => setBrollPickerOpen(false)}
              />
            )}

            {editingBrollLabel && (
              <ClipEditDialog
                label={editingBrollLabel}
                vocabulary={clipLabelVocabulary}
                categories={clipCategories}
                onCreateCategory={createClipCategory}
                onClose={() => setEditingBrollLabel(null)}
                onSave={saveBrollLibraryClip}
                onSaveAsNew={saveBrollLibraryClipAsNew}
              />
            )}

            {/* Clip editor modal — the inspector (player + scrub handles) opens
                here, distinct from the final-video stage above. */}
            <Dialog open={clipEditorOpen} onClose={closeClipEditor} maxWidth={false} fullWidth PaperProps={{ sx: { width: '76vw', maxWidth: '76vw', maxHeight: '86vh', m: 2 } }}>
              <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.25 }}>
                <ContentCutIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                Edit clip
                <IconButton aria-label="Close clip editor" onClick={closeClipEditor} sx={{ ml: 'auto' }}>
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent dividers sx={{ p: 2 }}>
                <Box ref={handleInspectorContainer} />
                {/* The full multi-track timeline is hidden by default (this modal
                    is about the one clip). Expand it to move/trim the clip in
                    context with the other tracks; the single timeline instance
                    portals in here only while expanded. */}
                <Button
                  size="small"
                  variant="text"
                  startIcon={modalTimelineExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  onClick={() => setModalTimelineExpanded((v) => !v)}
                  sx={{ mt: 2, textTransform: 'none' }}
                >
                  {modalTimelineExpanded ? 'Hide full timeline' : 'Show full timeline'}
                </Button>
                <Box ref={handleModalTimelineContainer} sx={{ mt: modalTimelineExpanded ? 1 : 0 }} />
              </DialogContent>
            </Dialog>

            {/* Renders the library into the rail slot and the inspector into the
                modal slot above; its own sub-dialogs and snackbar mount here. */}
            <ClipBrowser
              clips={visibleClips || []}
              selectedFileId={selectedFileId}
              selectedSegmentId={selectedSegmentId}
              onSelect={selectSourceClip}
              onSelectSegment={selectSavedClip}
              playerRef={playerRef}
              previewStatuses={previewStatuses}
              previewErrors={previewErrors}
              onRetryPreview={retryPreview}
              onExtractAudio={extractClipAudio}
              usedFileIds={usedFileIds}
              usedSegmentIds={usedSegmentIds}
              onAdd={cs => editorRef.current?.addClips(cs)}
              onRemoveFromTimeline={clip => editorRef.current?.removeSourceClip(clip.fileId) ?? false}
              segments={clipSegments}
              onSaveSegment={saveClipSegment}
              onDeleteSegment={removeClipSegment}
              onAddSegment={addSegmentToTimeline}
              onRemoveSegmentFromTimeline={segment => editorRef.current?.removeSegmentClip(segment.segmentId) ?? false}
              timelineClip={linkedTimelineClip}
              onTrimSelected={trimLinkedTimelineClip}
              onTrimSegment={trimSavedClip}
              clipLabels={clipLabels}
              clipLabelVocabulary={clipLabelVocabulary}
              clipLabelsLoading={clipLabelsLoading}
              onSaveClipLabel={saveClipLabel}
              clipCategories={clipCategories}
              onCreateClipCategory={createClipCategory}
              onAddToBrollLibrary={addToBrollLibrary}
              brollAssets={brollAssets}
              brollLabels={brollLabels}
              onEditBrollLibraryClip={setEditingBrollLabel}
              onOpenBrollPicker={() => setBrollPickerOpen(true)}
              onRemoveBrollAsset={removeBrollAsset}
              slots={{ library: libraryContainer, inspector: inspectorContainer }}
              notePanel={(
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                  <TextField
                    label="Note for editor"
                    multiline
                    fullWidth
                    minRows={6}
                    value={editorNote}
                    onChange={(e) => { setEditorNote(e.target.value); editorRef.current?.setSelectedClipNote(e.target.value); }}
                    disabled={!linkedTimelineClip}
                    placeholder={linkedTimelineClip
                      ? (linkedTimelineClip.kind === 'audio' ? 'e.g. duck under the voiceover' : 'e.g. on-screen text: "50% off"')
                      : 'Select a clip on the timeline to add a note'}
                    sx={{ flexGrow: 1, '& .MuiInputBase-root': { height: '100%', alignItems: 'flex-start' } }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    Included in the generated editor instructions for this clip.
                  </Typography>
                </Box>
              )}
            />
          </Box>
        )
      )}
      </Box>
      </VideoEditingFontScaleContext.Provider>
    </ThemeProvider>
  );
}
